// React Query hooks for the WB price-bands admin surface (Phase C4 Group 1).
//
// Pairs with /api/v1/wb/price-bands/* — list (non-paginated) / detail /
// create / patch / delete plus the nested /lines endpoints (GET, bulk
// PUT, single DELETE). `enc_id` is the encrypted scalar id throughout.
// DTOs mirror server/src/modules/wb/priceBands/types.ts.
//
// Server enforcement worth knowing about (we replicate the constraint in
// the UI but the server is authoritative):
//   - `type` is immutable post-create; PATCH must NOT include `type`,
//     `calc_op`, or `calc_value`.
//   - Calculated bands forbid `lines`; manual bands forbid the calc fields.
//   - `is_default_for_new_customers` is mutually exclusive within a tenant.
//   - `is_general_public = true` band is undeletable.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../api/client';
import { endpoints } from '../../../api/endpoints';
import { queryKeys } from '../../../api/queryKeys';

// -- Client-side types (mirror WbPriceBandDto / PriceBandLineDto) ---------

export type CalcOp =
  | 'plus_amount'
  | 'minus_amount'
  | 'plus_percent'
  | 'minus_percent';

export type PriceBandType = 'manual' | 'calculated';

export interface PriceBandLine {
  readonly product: {
    readonly enc_id: string;
    readonly name: string;
    readonly code: string;
  };
  readonly price: number;
}

export interface WbPriceBand {
  readonly enc_id: string;
  readonly name: string;
  readonly code: string;
  readonly type: PriceBandType;
  readonly calc_op: CalcOp | null;
  readonly calc_value: number | null;
  readonly is_general_public: boolean;
  readonly is_default_for_new_customers: boolean;
  readonly line_count: number;
  readonly lines?: PriceBandLine[];
  readonly created_at: string;
  readonly updated_at: string;
}

export interface CreatePriceBandInput {
  name: string;
  code: string;
  type: PriceBandType;
  calc_op?: CalcOp;
  calc_value?: number;
  is_default_for_new_customers?: boolean;
}

/** PATCH body — strictly excludes `type`, `calc_op`, `calc_value`. The
 *  server returns 422 if `type` is included and silently rejects the
 *  calc fields. Don't be tempted to widen this. */
export interface UpdatePriceBandInput {
  name?: string;
  code?: string;
  is_default_for_new_customers?: boolean;
}

export interface PriceBandLineInput {
  product_enc_id: string;
  price: number;
}

interface ListPriceBandsResponse {
  price_bands: WbPriceBand[];
}

interface DetailResponse {
  price_band: WbPriceBand;
}

interface LinesResponse {
  lines: PriceBandLine[];
}

// -- Queries ----------------------------------------------------------------

export function useWbPriceBands() {
  return useQuery<ListPriceBandsResponse>({
    queryKey: queryKeys.wb.priceBands(),
    queryFn: async () => {
      const res = await apiClient.get<ListPriceBandsResponse>(
        endpoints.wb.priceBands,
      );
      return res.data;
    },
    staleTime: 60_000,
  });
}

export function useWbPriceBand(encId: string | undefined) {
  const enabled = !!encId && encId !== 'new';
  return useQuery<DetailResponse>({
    queryKey: queryKeys.wb.priceBand(encId ?? ''),
    queryFn: async () => {
      if (!encId) throw new Error('useWbPriceBand called without enc_id');
      const res = await apiClient.get<DetailResponse>(
        endpoints.wb.priceBand(encId),
      );
      return res.data;
    },
    enabled,
    staleTime: 30_000,
  });
}

export function useWbPriceBandLines(encId: string | undefined) {
  const enabled = !!encId && encId !== 'new';
  return useQuery<LinesResponse>({
    queryKey: queryKeys.wb.priceBandLines(encId ?? ''),
    queryFn: async () => {
      if (!encId) throw new Error('useWbPriceBandLines called without enc_id');
      const res = await apiClient.get<LinesResponse>(
        endpoints.wb.priceBandLines(encId),
      );
      return res.data;
    },
    enabled,
    staleTime: 30_000,
  });
}

// -- Mutations --------------------------------------------------------------

export function useCreatePriceBand() {
  const qc = useQueryClient();
  return useMutation<DetailResponse, Error, CreatePriceBandInput>({
    mutationFn: async (body) => {
      const res = await apiClient.post<DetailResponse, CreatePriceBandInput>(
        endpoints.wb.priceBands,
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.priceBands() });
    },
  });
}

export function useUpdatePriceBand(encId: string) {
  const qc = useQueryClient();
  return useMutation<DetailResponse, Error, UpdatePriceBandInput>({
    mutationFn: async (body) => {
      const res = await apiClient.patch<DetailResponse, UpdatePriceBandInput>(
        endpoints.wb.priceBand(encId),
        body,
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.priceBands() });
      void qc.invalidateQueries({ queryKey: queryKeys.wb.priceBand(encId) });
    },
  });
}

export function useDeletePriceBand(encId: string) {
  const qc = useQueryClient();
  return useMutation<{ deleted: true }, Error, void>({
    mutationFn: async () => {
      const res = await apiClient.delete<{ deleted: true }>(
        endpoints.wb.priceBand(encId),
      );
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.priceBands() });
      void qc.invalidateQueries({ queryKey: queryKeys.wb.priceBand(encId) });
    },
  });
}

export function useReplacePriceBandLines(encId: string) {
  const qc = useQueryClient();
  return useMutation<LinesResponse, Error, PriceBandLineInput[]>({
    mutationFn: async (lines) => {
      const res = await apiClient.put<
        LinesResponse,
        { lines: PriceBandLineInput[] }
      >(endpoints.wb.priceBandLines(encId), { lines });
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.wb.priceBands() });
      void qc.invalidateQueries({ queryKey: queryKeys.wb.priceBand(encId) });
      void qc.invalidateQueries({
        queryKey: queryKeys.wb.priceBandLines(encId),
      });
    },
  });
}
